/** Публичный контракт персональных уведомлений; техническая доставка остаётся на сервере. */
export type NotificationKind =
  | 'instant'
  | 'digest'
  | 'analytics-report'
  | 'task';

/** Lifecycle BCF; строковые значения поддерживают старые ответы до cutover. */
export type EntityStatus = number | 'active' | 'deleted';

/** Точные ссылки отчёта без frozen roster и технического payload. */
export interface NotificationReportSource {
  type: 'analytics-report-ready';
  reportSid?: string | null;
  policyVersion?: string;
  templateVersion?: string;
  period: {from: string; to: string};
  comparisonPeriod: {from: string; to: string};
  timezone: string;
  manifest: Array<{
    runSid: string;
    teamRef?: {
      version: 'rop-subtree-v1' | 'rop-team-v2';
      batchPlanSid: string;
      teamMembershipSid: string;
      teamLeadUserSid: string;
      scopeHash: string;
    };
  }>;
  /** policyVersion=2: все негативные инсайты; v1: только требующие действий. */
  negativeActionableCount: number;
  positiveBestPracticeCount: number;
}

/** Серверно проверенный источник перехода из карточки. */
export type NotificationSource =
  | {
      type: 'attention-communication-ready';
      communicationId: string;
      /** Раздел карточки; нет/null — уведомления до появления поля (звонок). */
      communicationKind?: 'call' | 'meeting' | null;
    }
  | NotificationReportSource
  | {
      type: 'task-created' | 'task-assigned' | 'task-state-changed';
      taskSid: string;
    }
  | {
      type: 'task-mentioned';
      taskSid: string;
      commentSid: string;
      actorUserSid: string;
      policyVersion?: string;
      templateVersion?: string;
    }
  | {type: 'legacy-notification'; communicationId?: string | null};

/** Пользовательская проекция; delivery/metadata/dedupKey не являются публичным DTO. */
export interface BackendNotification {
  sid: string;
  workspaceId: string;
  userSid: string;
  kind: NotificationKind;
  /** instant: 1/2; task: приоритет задачи; старые task и отчёты: null. */
  priority: 1 | 2 | 'high' | 'medium' | 'low' | null;
  title: string;
  description: string;
  readAt: string | null;
  occurredAt: string;
  cursor?: string;
  source?: NotificationSource | null;
}

/** Параметры универсальной ленты, limit передаётся в POST body. */
export interface NotificationsListRequest {
  kind?: NotificationKind;
  before?: string;
  unreadOnly?: boolean;
  limit?: number;
}

/** Единственная публичная PATCH-мутация — собственное прочтение. */
export interface NotificationUpdateRequest {
  readAt: string | null;
}

/** Точный счётчик; при превышении бюджета сервер возвращает ошибку. */
export interface UnreadCountResponse {
  unreadCount: number;
}

/** Одна ограниченная порция массового прочтения. */
export interface ReadAllResponse {
  modifiedCount: number;
  hasMore: boolean;
  continuation?: string | null;
  unreadCount?: number | null;
}

/** Карточка дровера с безопасным маршрутом из typed source. */
export interface NotificationDisplay {
  id: string;
  kind: NotificationKind;
  priority: BackendNotification['priority'];
  unread: boolean;
  title: string;
  description: string;
  occurredAt: string;
  cursor?: string;
  callId?: string;
  /** `callId` — встреча: на дашборде открывается карточка встречи (`?meetingId=`). */
  isMeeting?: boolean;
  taskId?: string;
  sourcePath?: string;
  /** Сохранённые счётчики отчёта для компактной сводки в карточке. */
  reportCounts?: {negative: number; positive: number};
}

const COMM_LINK_RE = /\[.*?\]\(\/dashboard\?id=([a-f0-9-]{36})\)/i;

/** Адаптер сохраняет старый instant/digest и использует typed refs новых карточек. */
export const adaptNotification = (
  raw: BackendNotification,
): NotificationDisplay => {
  const source = raw.source;
  const reportCounts =
    source?.type === 'analytics-report-ready' &&
    (source.policyVersion === '2' || source.policyVersion === '3') &&
    Number.isInteger(source.negativeActionableCount) &&
    source.negativeActionableCount >= 0 &&
    Number.isInteger(source.positiveBestPracticeCount) &&
    source.positiveBestPracticeCount >= 0
      ? {
          negative: source.negativeActionableCount,
          positive: source.positiveBestPracticeCount,
        }
      : undefined;
  const callId =
    source && 'communicationId' in source
      ? (source.communicationId ?? undefined)
      : !source && raw.kind === 'instant'
        ? raw.description.match(COMM_LINK_RE)?.[1]
        : undefined;
  const isMeeting =
    source?.type === 'attention-communication-ready' &&
    source.communicationKind === 'meeting';
  const taskId = source && 'taskSid' in source ? source.taskSid : undefined;
  const sourcePath = callId
    ? `/dashboard?${isMeeting ? 'meetingId' : 'id'}=${encodeURIComponent(callId)}`
    : taskId
      ? `/tasks?taskId=${encodeURIComponent(taskId)}&workspaceId=${encodeURIComponent(raw.workspaceId)}`
      : source?.type === 'analytics-report-ready'
        ? source.reportSid
          ? `/dashboard?reportSid=${encodeURIComponent(source.reportSid)}&workspaceId=${encodeURIComponent(raw.workspaceId)}`
          : `/dashboard?notificationSid=${encodeURIComponent(raw.sid)}&queue=management`
        : undefined;
  return {
    id: raw.sid,
    kind: raw.kind,
    priority: raw.priority,
    unread: raw.readAt === null,
    title: raw.title,
    description: reportCounts
      ? `С негативной динамикой: ${reportCounts.negative}\nС позитивной динамикой: ${reportCounts.positive}`
      : raw.description,
    occurredAt: raw.occurredAt,
    cursor: raw.cursor,
    callId,
    isMeeting,
    taskId,
    sourcePath,
    reportCounts,
  };
};

/** Канал внешней доставки. */
export type ChannelName = 'telegram' | 'max';
/** Поддерживаемые каналы для UI. */
export const CHANNEL_NAMES = [
  'telegram',
  'max',
] as const satisfies readonly ChannelName[];
/** Состояние отдельного подключения. */
export type ConnectionState =
  | 'pending_verification'
  | 'connected'
  | 'disconnected';
/** Выбор доставки по каналам. */
export type ChannelToggle = Record<ChannelName, boolean>;
/** Очередь внимания сохраняет две независимые настройки приоритета. */
export interface SectionSettings {
  enabled: boolean;
  p1: ChannelToggle;
  p2: ChannelToggle;
}
/** Отчёты и задачи не имеют искусственного приоритета звонка. */
export interface SimpleSectionSettings {
  enabled: boolean;
  channels: ChannelToggle;
}
/** Известные секции; отсутствие attention не включает старую call-рассылку. */
export interface NotificationSections {
  'attention-queue'?: SectionSettings;
  'analytics-reports': SimpleSectionSettings;
  tasks: SimpleSectionSettings;
}
/** Собственные настройки пользователя. */
export interface NotificationSettings {
  sid: string;
  userSid: string;
  sections: NotificationSections;
  reportWorkspaceSids: string[];
  status: EntityStatus;
  createdAt: string;
  updatedAt: string | null;
}
/** Patch известных секций; пропущенные секции и вложенные каналы сохраняются. */
export interface UpdateSettingsDTO {
  sections?: {
    'attention-queue'?: Partial<SectionSettings>;
    'analytics-reports'?: Partial<SimpleSectionSettings>;
    tasks?: Partial<SimpleSectionSettings>;
  };
  reportWorkspaceSids?: string[];
}

/** Канал в списке GET /notifications/settings/channels. */
export interface ChannelInfo {
  /** SID подключения (для DELETE /channels/{connectionSid}). */
  connectionSid: string;
  channel: ChannelName;
  connectionState: ConnectionState;
}

/**
 * Ответ POST /notifications/settings/channels/{channel}/init.
 * Контракт общий для всех каналов-ботов (telegram, max).
 */
export interface ChannelInitResponse {
  /** 6-символьный код верификации (TTL 5 мин). */
  code: string;
  /** Ссылка на бота — пользователь открывает, пишет /start + код. */
  botLink: string;
}
